import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Account } from '@core/auth/account.model';

import { AccountService } from '@core/auth/account.service';
import { LoginService } from '@core/login/login.service';
import { DataUtils } from '@core/util/data-util.service';
import { DataUtilsService } from '@flowinn/services/data-utils.service';
import { TranslateDirective } from '@language/translate.directive';
import { ProgramEnum } from '@model/enumerations/program-enum.model';
import { ProgramModality } from '@model/enumerations/program-modality.model';
import { ProgramFaqCategory } from '@model/program-faq-category.model';
import { ProgramFaq } from '@model/program-faq.model';
import { ProgramGeneralInformation } from '@model/program-general-information.model';
import { ProgramInstitutionEntityInformation } from '@model/program-institution-entity-information.model';
import { ProgramLegislation } from '@model/program-legislation.model';
import { ProgramLink } from '@model/program-link.model';
import { ProgramYoungInformation } from '@model/program-young-information.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ProgramFaqCategoryService } from '@programs/program-faq-category/program-faq-category.service';
import { ProgramFaqService } from '@programs/program-faq/program-faq.service';
import { ProgramGeneralInformationService } from '@programs/program-general-information/program-general-information.service';
import { ProgramInstitutionEntityInformationService } from '@programs/program-institution-entity-information/program-institution-entity-information.service';
import { ProgramLegislationService } from '@programs/program-legislation/program-legislation.service';
import { ProgramLinkService } from '@programs/program-link/program-link.service';
import { ProgramYoungInformationService } from '@programs/program-young-information/program-young-information.service';
import { FrontendModalComponent } from 'app/frontend/frontend-modal.component';
import { VideoModalComponent } from 'app/frontend/video-modal.component';
import { TabsetComponent, TabsModule } from 'ngx-bootstrap/tabs';
import { OrderModule } from 'ngx-order-pipe';

@Component({
  selector: 'jhi-abstract-frontend',
  templateUrl: './abstract-frontend.component.html',
  styleUrls: ['./abstract-frontend.scss'],
  imports: [TabsModule, NgClass, TranslateDirective, RouterLink, NgIf, NgForOf, TranslateModule, OrderModule],
  standalone: true
})
export class AbstractFrontendComponent implements OnInit {
  account: Account | null = null;
  currentLanguage: string | undefined;
  programEnum: ProgramEnum | null = null;
  programEnumLower: string | null = null;
  modalities: ProgramModality[] = [];
  youngModalities: ProgramModality[] = [];
  entitiesModalities: ProgramModality[] = [];
  selectedModality?: ProgramModality;
  mainImage: string | undefined;
  mainBanner: string | undefined;
  active = 0;
  legislation: ProgramLegislation[] = [];
  links: ProgramLink[] = [];
  youngInformation: ProgramYoungInformation[] = [];
  institutionInformation: ProgramInstitutionEntityInformation[] = [];
  generalInformation?: ProgramGeneralInformation;
  faqs: ProgramFaq[] = [];
  faqsCategories: ProgramFaqCategory[] = [];
  enum = ProgramEnum;
  ProgramModality = ProgramModality;
  @ViewChild('modalityTabs', { static: false }) modalityTabs: TabsetComponent | undefined;
  @ViewChild('programTabs', { static: false }) programTabs: TabsetComponent | undefined;

  constructor(
    private activatedRoute: ActivatedRoute,
    private accountService: AccountService,
    private translateService: TranslateService,
    private router: Router,
    protected dataUtils: DataUtils,
    protected dataUtilsService: DataUtilsService,
    protected modalService: NgbModal,
    private programLegislationService: ProgramLegislationService,
    private programLinkService: ProgramLinkService,
    private programYoungInformationService: ProgramYoungInformationService,
    private programInstitutionEntityInformationService: ProgramInstitutionEntityInformationService,
    private programGeneralInformationService: ProgramGeneralInformationService,
    private programFaqService: ProgramFaqService,
    private programFaqCategoryService: ProgramFaqCategoryService,
    private loginService: LoginService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data.subscribe(({ programEnum, modalities }) => {
      this.entitiesModalities = [];
      this.youngModalities = [];
      this.programEnum = programEnum;
      if (this.programEnum === ProgramEnum.EUROSCOLA) {
        this.active = 1;
      }
      this.programEnumLower = (this.programEnum + '').toLowerCase();
      this.mainImage = '/content/images/' + this.programEnumLower + '/logotipo.png?cache=' + new Date().getMilliseconds();
      this.mainBanner = '/content/images/' + this.programEnumLower + '/front.png?cache=' + new Date().getMilliseconds();
      this.modalities = modalities;
      if (this.programEnum === ProgramEnum.CUIDA_TE2) {
        this.entitiesModalities = [ProgramModality.MOBILE_UNIT, ProgramModality.COMMUNITY_INTERVENTION, ProgramModality.AWARENESS_AND_INFORMATION];
        this.youngModalities = [ProgramModality.HEALTH_OFFICES, ProgramModality.CONVERSATION_CHANNEL];
      } else if (this.programEnum === ProgramEnum.CUIDA_TE) {
        this.modalities = [ProgramModality.MOBILE_UNIT, ProgramModality.HEALTH_OFFICES, ProgramModality.HEALTH_EDUCATION, ProgramModality.ONLINE_SEXUALITY];
      }
      if (this.modalities && this.modalities.length > 0) {
        this.selectedModality = this.modalities[0];
      }
      this.getModalityInfo();
    });
    this.currentLanguage = this.translateService.currentLang;
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  getModalityInfo(): void {
    const query: any = { size: 100, 'programEnum.equals': this.programEnum };
    if (this.selectedModality) {
      query['programModality.equals'] = this.selectedModality;
    }
    this.legislation = [];
    this.links = [];
    this.youngInformation = [];
    this.institutionInformation = [];
    this.generalInformation = undefined;
    this.faqs = [];
    this.faqsCategories = [];
    this.programLegislationService.query(query).subscribe(res => (this.legislation = res.body || []));
    this.programLinkService.query(query).subscribe(res => (this.links = res.body || []));
    this.programYoungInformationService.query(query).subscribe(res => {
      this.youngInformation = res.body || [];
    });
    this.programInstitutionEntityInformationService.query(query).subscribe(res => {
      this.institutionInformation = res.body || [];
    });
    this.programGeneralInformationService.query(query).subscribe(res => {
      if (res.body && res.body.length > 0) {
        this.generalInformation = res.body[0];
      }
    });
    this.programFaqService.query(query).subscribe(res => (this.faqs = res.body || []));
    this.programFaqCategoryService.query(query).subscribe(res => (this.faqsCategories = res.body || []));
  }

  selectModalityTab(tabId: number, modality: ProgramModality): void {
    if (this.modalityTabs) {
      this.modalityTabs.tabs[tabId].active = true;
    }
    this.selectedModality = modality;
    this.getModalityInfo();
  }

  isModalityActive(tabId: number): boolean {
    return this.modalityTabs?.tabs[tabId]?.active === true;
  }

  getLink(programEnum: ProgramEnum | null): void {
    switch (programEnum) {
      case ProgramEnum.AGORA_NOS:
        void this.router.navigate(['agora-nos/acoes']);
        return;
      case ProgramEnum.SETENTAJA:
        void this.router.navigate(['70-ja/acoes']);
        return;
      case ProgramEnum.NAMORAR:
        void this.router.navigate(['namorar-com-fair-play/acoes']);
        return;
      case ProgramEnum.NAVEGAS:
        void this.router.navigate(['navegas-em-seguranca/acoes']);
        return;
      case ProgramEnum.SOUDIGITAL:
        void this.router.navigate(['geracoes-em-rede/acoes']);
        return;
      case ProgramEnum.CTI:
        // void this.router.navigate(['agora-nos/actions']);
        return;
      case ProgramEnum.EUROSCOLA:
        // void this.router.navigate(['agora-nos/actions']);
        return;
      case ProgramEnum.FERIAS:
        void this.router.navigate(['ferias/acoes']);
        return;
      case ProgramEnum.FLORESTAS:
        void this.router.navigate(['vjnf/acoes']);
        return;
      case ProgramEnum.GERACAOZ:
        void this.router.navigate(['geracaoz/acoes']);
        return;
      case ProgramEnum.OTL:
        void this.router.navigate(['otl/acoes']);
        return;
      case ProgramEnum.PASSE_JOVEM:
        // void this.router.navigate(['agora-nos/actions']);
        return;
      default:
        // void this.router.navigate(['agora-nos/actions']);
        return;
    }
  }

  selectProgramTab(tabId: number): void {
    this.active = tabId;
    if (this.programTabs) {
      this.programTabs.tabs[tabId].active = true;
    }
  }

  login(): void {
    this.loginService.login();
  }

  openFile(contentType: string, base64String: string, filename: string): void {
    this.dataUtilsService.openFile(contentType, base64String, filename);
  }

  getText(value?: string): string {
    if (value && value.length > 200) {
      return value.substr(0, 200);
    }
    return value || '';
  }

  openModal(title?: string, message?: any): void {
    const modalRef = this.modalService.open(FrontendModalComponent, { size: 'lg', backdrop: 'static', windowClass: 'frontend-modal' });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.title = title;
  }

  viewInfographic(): void {
    this.modalService.open(VideoModalComponent, { size: 'lg', backdrop: 'static', windowClass: 'frontend-modal' });
  }
}
